import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight, CalendarDays, LayoutGrid, List as ListIcon, X, MapPin, ChevronDown, Pencil, Lock, History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { IfCan } from "@/components/PermGate";
import api, { formatApiError } from "@/lib/api";
import { formatDMY } from "@/lib/date";
import RegionSelect from "@/components/RegionSelect";
import RegionFilter from "@/components/RegionFilter";
import { useClashGuard } from "@/lib/schedule";

const STATUSES = ["Scheduled", "Dispatched", "In Progress", "Returned", "Converted to Sale", "Completed", "Cancelled"];
const EDIT_STATUSES = ["Scheduled", "In Progress", "Completed", "Cancelled"];

const EditBtn = ({ b, onEdit, testId, className = "" }) => (b?.can_edit ? (
  <button type="button" title="Edit booking" onClick={(e) => { e.stopPropagation(); onEdit?.(b); }} data-testid={testId}
    className={`inline-flex items-center justify-center w-6 h-6 rounded bg-white/90 border border-slate-200 text-slate-600 hover:text-indigo-700 hover:border-indigo-300 transition-colors ${className}`}>
    <Pencil className="w-3 h-3" />
  </button>
) : null);
const STATUS_STYLES = {
  "Scheduled": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Dispatched": "bg-amber-50 text-amber-700 border-amber-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
  "Returned": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Converted to Sale": "bg-violet-50 text-violet-700 border-violet-200",
  "Completed": "bg-teal-50 text-teal-700 border-teal-200",
  "Cancelled": "bg-rose-50 text-rose-600 border-rose-200 line-through",
};

function fmtDate(iso) {
  return formatDMY(iso);
}
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfWeek(d) { const x = new Date(d); const day = x.getDay(); x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

function buildMonthGrid(anchor) {
  const first = startOfMonth(anchor);
  const start = startOfWeek(first);
  const cells = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(start, i));
  return cells;
}

export default function Bookings() {
  const [view, setView] = useState("month"); // month | week | list | byregion
  const [anchor, setAnchor] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [available, setAvailable] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null); // booking obj
  const [regionFilter, setRegionFilter] = useState("");
  const [schedMeta, setSchedMeta] = useState(null);
  const [editing, setEditing] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const guard = useClashGuard();

  const load = async () => {
    try {
      const bParams = regionFilter && regionFilter !== "__none__" ? `?region_id=${encodeURIComponent(regionFilter)}` : "";
      const [b, av, l, u, sm] = await Promise.all([
        api.get(`/bookings${bParams}`),
        api.get("/inventory/available"),
        api.get("/leads"),
        api.get("/users?active_only=true"),
        api.get("/schedule/meta"),
      ]);
      const bData = regionFilter === "__none__" ? b.data.filter((x) => !x.region_id) : b.data;
      setBookings(bData);
      setAvailable(av.data);
      setLeads(l.data);
      setUsers(u.data);
      setSchedMeta(sm.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [regionFilter]);

  // Deep link from the schedule: /bookings?booking=<id> opens that booking (?edit=<id> opens the edit form).
  useEffect(() => {
    const id = searchParams.get("booking") || searchParams.get("edit");
    if (!id) return;
    const asEdit = !!searchParams.get("edit");
    api.get(`/bookings/${id}`).then((r) => (asEdit && r.data.can_edit ? setEditing(r.data) : setDetail(r.data))).catch((e) => toast.error(formatApiError(e)));
    const next = new URLSearchParams(searchParams); next.delete("booking"); next.delete("edit"); setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [searchParams]);

  const byDay = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      const k = b.scheduled_at ? ymd(new Date(b.scheduled_at)) : null;
      if (!k) continue;
      (map[k] = map[k] || []).push(b);
    }
    return map;
  }, [bookings]);

  const monthCells = view === "month" ? buildMonthGrid(anchor) : [];
  const weekCells = view === "week" ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)) : [];

  const shift = (dir) => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7 * dir);
    setAnchor(d);
  };

  return (
    <div data-testid="bookings-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Scheduling</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">Demo bookings</h1>
          <p className="text-slate-500 text-sm mt-1 hidden md:block">Schedule machine demos and prevent double-booking of physical units.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="hidden md:flex bg-white border border-slate-200 rounded-md p-0.5">
            <button onClick={() => setView("month")} data-testid="view-month-btn" className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 ${view === "month" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
              <LayoutGrid className="w-3.5 h-3.5" /> Month
            </button>
            <button onClick={() => setView("week")} data-testid="view-week-btn" className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 ${view === "week" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
              <CalendarDays className="w-3.5 h-3.5" /> Week
            </button>
            <button onClick={() => setView("list")} data-testid="view-list-btn" className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 ${view === "list" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
              <ListIcon className="w-3.5 h-3.5" /> List
            </button>
            <button onClick={() => setView("byregion")} data-testid="view-byregion-btn" className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 ${view === "byregion" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
              <MapPin className="w-3.5 h-3.5" /> By Region
            </button>
          </div>
          <RegionFilter value={regionFilter} onChange={setRegionFilter} testId="bookings-region-filter" />
          <IfCan module="bookings" level="edit"><Button onClick={() => setCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 w-full md:w-auto" data-testid="new-booking-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New booking
          </Button></IfCan>
        </div>
      </div>

      {/* Mobile: always show upcoming list as cards, regardless of desktop view state */}
      <div className="md:hidden bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
          <CalendarDays className="w-4 h-4" /> Upcoming demos
        </div>
        <ul className="divide-y divide-slate-100">
          {bookings.length === 0 ? (
            <li className="text-center text-slate-400 py-10" data-testid="bookings-empty-m">
              <CalendarDays className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No demo bookings yet.
            </li>
          ) : bookings.map((b) => (
            <li key={b.id} onClick={() => setDetail(b)} className="p-4 active:bg-slate-50" data-testid={`booking-card-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{b.client?.company_name || "—"}</div>
                  <div className="text-xs text-slate-500 truncate">{b.machine_catalog?.model_name} {b.inventory?.serial_number ? `· #${b.inventory.serial_number}` : ""}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <EditBtn b={b} onEdit={setEditing} testId={`booking-card-edit-${b.id}`} />
                  <Badge className={`border ${STATUS_STYLES[b.status] || ""} shrink-0`} variant="outline">{b.status}</Badge>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                <span><CalendarDays className="w-3 h-3 inline mr-1" />{fmtDate(b.scheduled_at)} · {fmtTime(b.scheduled_at)}</span>
                {b.assigned_user?.name && <span>· {b.assigned_user.name}</span>}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {view !== "list" && (
        <div className="hidden md:flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="cal-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="cal-today-btn">Today</Button>
            <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="cal-next-btn"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div className="font-heading font-semibold text-lg text-slate-900" data-testid="cal-anchor-label">
            {anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
          </div>
          <div className="w-32" />
        </div>
      )}

      {view === "month" && (
        <div className="hidden md:block bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="calendar-month">
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthCells.map((d, idx) => {
              const inMonth = d.getMonth() === anchor.getMonth();
              const isToday = ymd(d) === ymd(new Date());
              const dayBookings = byDay[ymd(d)] || [];
              return (
                <div key={idx} className={`border-b border-r border-slate-100 min-h-[110px] p-1.5 ${inMonth ? "bg-white" : "bg-slate-50/50"}`} data-testid={`cal-day-${ymd(d)}`}>
                  <div className={`text-xs font-medium mb-1 ${isToday ? "text-indigo-600" : inMonth ? "text-slate-700" : "text-slate-400"}`}>
                    {d.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayBookings.slice(0, 3).map((b) => (
                      <div key={b.id} role="button" tabIndex={0} onClick={() => setDetail(b)} data-testid={`cal-event-${b.id}`}
                        className={`group relative w-full text-left rounded px-1.5 py-1 border text-[10px] leading-tight cursor-pointer ${STATUS_STYLES[b.status] || ""} hover:brightness-95 transition-[filter] duration-150`}>
                        <div className="font-semibold truncate pr-4">{b.client?.company_name || "—"}</div>
                        <div className="truncate opacity-80">{b.machine_catalog?.model_name} · {fmtTime(b.scheduled_at)}</div>
                        <EditBtn b={b} onEdit={setEditing} testId={`cal-event-edit-${b.id}`} className="absolute top-0.5 right-0.5 opacity-70 group-hover:opacity-100" />
                      </div>
                    ))}
                    {dayBookings.length > 3 && <div className="text-[10px] text-slate-500 px-1">+{dayBookings.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="hidden md:block bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="calendar-week">
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
            {weekCells.map((d) => (
              <div key={d.toString()} className="px-3 py-2 text-xs font-semibold text-slate-600">
                <div className="uppercase tracking-wider">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                <div className="text-slate-500">{d.getDate()}/{d.getMonth() + 1}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {weekCells.map((d) => {
              const dayBookings = byDay[ymd(d)] || [];
              return (
                <div key={d.toString()} className="border-r border-slate-100 min-h-[300px] p-2 space-y-1.5" data-testid={`cal-day-${ymd(d)}`}>
                  {dayBookings.length === 0 && <div className="text-[10px] text-slate-300">No demos</div>}
                  {dayBookings.map((b) => (
                    <div key={b.id} role="button" tabIndex={0} onClick={() => setDetail(b)} data-testid={`cal-event-${b.id}`}
                      className={`group relative w-full text-left rounded px-2 py-1.5 border text-xs cursor-pointer ${STATUS_STYLES[b.status] || ""} hover:brightness-95 transition-[filter] duration-150`}>
                      <div className="font-semibold">{fmtTime(b.scheduled_at)}</div>
                      <div className="font-medium pr-5">{b.client?.company_name}</div>
                      <div className="text-[11px] opacity-80">{b.machine_catalog?.model_name}</div>
                      <EditBtn b={b} onEdit={setEditing} testId={`cal-event-edit-${b.id}`} className="absolute top-1 right-1 opacity-70 group-hover:opacity-100" />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "list" && (
        <div className="hidden md:block bg-white border border-slate-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">When</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Region</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Machine (serial)</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10" data-testid="bookings-empty">No demo bookings yet.</TableCell></TableRow>
              ) : (
                bookings.map((b) => (
                  <TableRow key={b.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(b)} data-testid={`booking-row-${b.id}`}>
                    <TableCell className="text-sm text-slate-900">{fmtDate(b.scheduled_at)} <span className="text-slate-500">{fmtTime(b.scheduled_at)}</span></TableCell>
                    <TableCell className="text-sm font-medium">{b.client?.company_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {b.region_name ? (
                        <span className="inline-flex items-center gap-1 text-xs text-sky-700 bg-sky-50 border border-sky-100 rounded px-1.5 py-0.5">
                          <MapPin className="w-3 h-3" /> {b.region_name}
                        </span>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="text-slate-900">{b.machine_catalog?.model_name || "—"}</div>
                      <div className="text-xs font-mono text-slate-500">{b.inventory?.serial_number}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{b.assigned_user?.name || "—"}</TableCell>
                    <TableCell><Badge className={`border ${STATUS_STYLES[b.status] || ""}`} variant="outline">{b.status}</Badge></TableCell>
                    <TableCell><EditBtn b={b} onEdit={setEditing} testId={`booking-row-edit-${b.id}`} /></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {view === "byregion" && (
        <BookingsByRegion bookings={bookings} onOpen={(b) => setDetail(b)} onEdit={setEditing} STATUS_STYLES={STATUS_STYLES} fmtDate={fmtDate} fmtTime={fmtTime} />
      )}

      <BookingFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        leads={leads}
        available={available}
        schedMeta={schedMeta}
        guard={guard}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
      <BookingFormDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        booking={editing}
        leads={leads}
        available={available}
        schedMeta={schedMeta}
        guard={guard}
        onCreated={(saved) => { setEditing(null); if (detail) setDetail(saved); load(); }}
      />

      <BookingDetailDialog booking={detail} users={users} guard={guard} onEdit={(b) => setEditing(b)} onClose={() => setDetail(null)} onChanged={(nb) => { if (nb) setDetail(nb); else setDetail(null); load(); }} />
      {guard.dialog}
    </div>
  );
}

// ---------- Create dialog ----------
function BookingFormDialog({ open, onOpenChange, leads, available, schedMeta, guard, onCreated, booking }) {
  const isEdit = !!booking;
  const leadTouched = useRef(false);
  const [leadId, setLeadId] = useState("");
  const [catalogId, setCatalogId] = useState("");
  const [inventoryId, setInventoryId] = useState("");  // "" = auto-pick a free unit
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("10:00");
  const [expectedReturn, setExpectedReturn] = useState("");
  const [expectedReturnTime, setExpectedReturnTime] = useState("18:00");
  const [assignedTo, setAssignedTo] = useState("");
  const [visitFrom, setVisitFrom] = useState("10:00");
  const [visitTo, setVisitTo] = useState("12:00");
  const engineers = schedMeta?.engineers || [];
  // In edit mode the booking's current lead / engineer must be selectable even if outside the editor's own lists.
  const leadOptions = isEdit && booking.lead_id && !leads.some((l) => l.id === booking.lead_id)
    ? [{ id: booking.lead_id, company_name: booking.client?.company_name || booking.lead?.company_name, stage: booking.lead?.stage || "" }, ...leads]
    : leads;
  const engineerOptions = isEdit && booking.assigned_user && !engineers.some((u) => u.id === booking.assigned_to)
    ? [booking.assigned_user, ...engineers]
    : engineers;
  const canAssignOthers = !!schedMeta?.can_assign_others;
  const [contactPerson, setContactPerson] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [status, setStatus] = useState("Scheduled");
  const [errors, setErrors] = useState({});
  useEffect(() => {
    if (open && !isEdit && !canAssignOthers && schedMeta?.me) setAssignedTo(schedMeta.me);
  }, [open, isEdit, canAssignOthers, schedMeta]);
  // Visit slot follows the demo start time by default (2 hours).
  const onTimeChange = (t) => {
    setScheduledTime(t);
    if (!t) return;
    const [h, m] = t.split(":").map(Number);
    setVisitFrom(t);
    setVisitTo(`${String(Math.min(23, h + 2)).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  };
  const onCatalogChange = (v) => { setCatalogId(v); setInventoryId(""); };
  const onLeadChange = (v) => { leadTouched.current = true; setLeadId(v); setErrors((e) => ({ ...e, lead: "" })); };
  const [notes, setNotes] = useState("");
  const [addrChoice, setAddrChoice] = useState("");
  const [override, setOverride] = useState({ label: "", line: "", city: "", state: "", pincode: "" });
  const [saving, setSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  const [client, setClient] = useState(null);
  const [onlyFree, setOnlyFree] = useState(true);
  const [availWindow, setAvailWindow] = useState(null);  // window-aware /inventory/available response
  const [regionId, setRegionId] = useState("");

  // Units eligible under the chosen model — prefer the window-aware payload
  // when we have it, so the ``busy_in_window`` flags are up to date.
  const modelGroup = useMemo(() => {
    const src = availWindow || available;
    return src.find((g) => g.catalog_id === catalogId);
  }, [availWindow, available, catalogId]);
  // In edit mode the unit already on this booking is always selectable.
  const currentUnit = isEdit && booking.inventory && booking.catalog_id === catalogId
    ? { id: booking.inventory_id, serial_number: booking.inventory.serial_number, location: booking.inventory.location, busy_in_window: false, current: true }
    : null;
  const modelUnits = [
    ...(currentUnit ? [currentUnit] : []),
    ...(modelGroup?.units || []).filter((u) => u.id !== currentUnit?.id),
  ];
  const modelOptions = isEdit && booking.machine_catalog && !available.some((g) => g.catalog_id === booking.catalog_id)
    ? [{ catalog_id: booking.catalog_id, model_name: booking.machine_catalog.model_name, manufacturer: booking.machine_catalog.manufacturer }, ...available]
    : available;
  const windowComplete = !!(scheduledDate && scheduledTime && expectedReturn && expectedReturnTime);
  const visibleUnits = onlyFree && windowComplete
    ? modelUnits.filter((u) => !u.busy_in_window)
    : modelUnits;
  const hiddenCount = onlyFree && windowComplete ? modelUnits.length - visibleUnits.length : 0;

  const pad2 = (n) => String(n).padStart(2, "0");
  const dParts = (iso) => { const d = new Date(iso); return [`${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`, `${pad2(d.getHours())}:${pad2(d.getMinutes())}`]; };
  useEffect(() => {
    if (!open) {
      leadTouched.current = false;
      setLeadId(""); setCatalogId(""); setInventoryId(""); setScheduledDate(""); setScheduledTime("10:00");
      setExpectedReturn(""); setExpectedReturnTime("18:00"); setAssignedTo(""); setNotes(""); setAddrChoice(""); setClient(null);
      setOverride({ label: "", line: "", city: "", state: "", pincode: "" });
      setScheduleError(""); setOnlyFree(true); setAvailWindow(null); setRegionId("");
      setContactPerson(""); setContactPhone(""); setStatus("Scheduled"); setErrors({}); setVisitFrom("10:00"); setVisitTo("12:00");
      return;
    }
    if (!booking) return;
    const b = booking;
    setLeadId(b.lead_id || ""); setCatalogId(b.catalog_id || ""); setInventoryId(b.inventory_id || "");
    const [sd, st] = dParts(b.scheduled_at); setScheduledDate(sd); setScheduledTime(st);
    const ret = b.expected_return_date?.includes("T") ? b.expected_return_date : `${b.expected_return_date}T18:00:00`;
    const [rd, rt] = dParts(ret); setExpectedReturn(rd); setExpectedReturnTime(rt);
    if (b.visit_start) { setVisitFrom(dParts(b.visit_start)[1]); setVisitTo(dParts(b.visit_end)[1]); }
    setAssignedTo(b.assigned_to || ""); setNotes(b.outcome_notes || ""); setRegionId(b.region_id || "");
    setAddrChoice("__override__");
    setOverride({ label: "", line: "", city: "", state: "", pincode: "", ...(b.site_address || {}) });
    setContactPerson(b.contact_person || ""); setContactPhone(b.contact_phone || ""); setStatus(b.status || "Scheduled");
    // eslint-disable-next-line
  }, [open, booking]);

  // Inherit region from lead unless the user explicitly picks one.
  const selectedLead = leads.find((l) => l.id === leadId);
  useEffect(() => {
    if (selectedLead?.region_id && !regionId) setRegionId(selectedLead.region_id);
    if (leadTouched.current && selectedLead) { setContactPerson(""); setContactPhone(""); }
    // eslint-disable-next-line
  }, [leadId]);

  // When the full window is chosen, ask the server which serials are busy so
  // the "Only free serials" toggle has accurate data.
  useEffect(() => {
    if (!windowComplete) { setAvailWindow(null); return; }
    const start = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
    const end = new Date(`${expectedReturn}T${expectedReturnTime}:00`).toISOString();
    let cancelled = false;
    (async () => {
      try {
        const ex = isEdit ? `&exclude_booking_id=${booking.id}` : "";
        const r = await api.get(`/inventory/available?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}${ex}`);
        if (!cancelled) setAvailWindow(r.data);
      } catch (_e) { if (!cancelled) setAvailWindow(null); }
    })();
    return () => { cancelled = true; };
  }, [windowComplete, scheduledDate, scheduledTime, expectedReturn, expectedReturnTime]);

  // If the currently picked serial gets filtered out, drop the selection.
  useEffect(() => {
    if (inventoryId && !visibleUnits.some((u) => u.id === inventoryId)) {
      setInventoryId("");
    }
  }, [visibleUnits, inventoryId]);

  // Live validation of scheduled datetime (in edit mode only when it changed)
  useEffect(() => {
    if (!scheduledDate) { setScheduleError(""); return; }
    const dt = new Date(`${scheduledDate}T${scheduledTime || "00:00"}:00`);
    if (isNaN(dt.getTime())) { setScheduleError(""); return; }
    if (isEdit && Math.abs(dt.getTime() - new Date(booking.scheduled_at).getTime()) < 60_000) { setScheduleError(""); return; }
    if (dt.getTime() < Date.now() - 60_000) {
      setScheduleError("Demo cannot be scheduled in the past");
    } else {
      setScheduleError("");
    }
  }, [scheduledDate, scheduledTime]);

  useEffect(() => {
    (async () => {
      if (!leadId) { setClient(null); setAddrChoice(""); return; }
      const lead = leads.find((l) => l.id === leadId);
      if (!lead?.client_id) return;
      try {
        const r = await api.get(`/clients/${lead.client_id}`);
        setClient(r.data);
        if (!isEdit || leadTouched.current) setAddrChoice("__billing__");
      } catch (_e) {
        // No Clients access: fall back to the read-only clients lookup (addresses only).
        const all = await api.get("/clients").catch(() => ({ data: [] }));
        const c = all.data.find((x) => x.id === lead.client_id);
        if (c) { setClient(c); if (!isEdit || leadTouched.current) setAddrChoice("__billing__"); }
      }
    })();
    // eslint-disable-next-line
  }, [leadId, leads]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!leadId) errs.lead = "Client is required";
    if (!catalogId) errs.machine = "Machine is required";
    if (!scheduledDate) errs.scheduled = "Pick the demo date";
    if (!expectedReturn) errs.ret = "Pick the expected return date";
    if (!assignedTo) errs.engineer = "Assign an engineer";
    const scheduledDt = new Date(`${scheduledDate}T${scheduledTime}:00`);
    const returnDt = new Date(`${expectedReturn}T${expectedReturnTime || "18:00"}:00`);
    if (scheduledDate && expectedReturn && (isNaN(returnDt.getTime()) || returnDt.getTime() <= scheduledDt.getTime())) {
      errs.ret = "Expected return must be after the scheduled time";
    }
    const visitStartDt = new Date(`${scheduledDate}T${visitFrom}:00`);
    const visitEndDt = new Date(`${scheduledDate}T${visitTo}:00`);
    if (scheduledDate && !(visitEndDt > visitStartDt)) errs.visit = "Visit end must be after visit start";
    if (addrChoice === "__override__" && !override.line) errs.address = "Enter the address line";
    if (!addrChoice) errs.address = "Choose a site address";
    setErrors(errs);
    if (Object.keys(errs).length || scheduleError) { toast.error("Please fix the highlighted fields"); return; }
    let site_address;
    if (addrChoice === "__override__") {
      site_address = override;
    } else if (addrChoice === "__billing__") {
      site_address = { label: "Billing address", line: client?.billing_address || "" };
    } else {
      const a = (client?.shipping_addresses || []).find((x) => x.id === addrChoice);
      if (!a) { setErrors({ address: "Choose a site address" }); return; }
      site_address = { label: a.label, line: a.line, city: a.city, state: a.state, pincode: a.pincode };
    }
    const scheduled_at = scheduledDt.toISOString();
    const expected_return_at = returnDt.toISOString();
    setSaving(true);
    try {
      const payload = {
        lead_id: leadId,
        site_address,
        scheduled_at,
        expected_return_date: expected_return_at,
        assigned_to: assignedTo,
        visit_start: visitStartDt.toISOString(),
        visit_end: visitEndDt.toISOString(),
        outcome_notes: notes,
        status: isEdit ? status : "Scheduled",
        region_id: regionId || "",
        contact_person: contactPerson,
        contact_phone: contactPhone,
      };
      // Send inventory_id if a specific serial was picked, otherwise let the
      // server auto-pick a free unit of the model.
      if (inventoryId) payload.inventory_id = inventoryId;
      else payload.catalog_id = catalogId;
      const res = await guard.run((allow) => (isEdit
        ? api.put(`/bookings/${booking.id}${allow ? "?allow_clash=true" : ""}`, payload)
        : api.post(`/bookings${allow ? "?allow_clash=true" : ""}`, payload)));
      if (!res) return;
      toast.success(isEdit ? "Booking updated" : (inventoryId ? "Booking created — this serial is reserved for your window." : "Booking created — a demo unit is reserved for your window."));
      onCreated?.(res.data);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "booking-edit-dialog" : "booking-create-dialog"}>
        <DialogHeader><DialogTitle>{isEdit ? "Edit demo booking" : "New demo booking"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Client (lead) *</Label>
            <Select value={leadId} onValueChange={onLeadChange}>
              <SelectTrigger className={`mt-1 ${errors.lead ? "border-red-500" : ""}`} data-testid="booking-lead-select"><SelectValue placeholder="Select a lead" /></SelectTrigger>
              <SelectContent>
                {leadOptions.length === 0 && <SelectItem value="none" disabled>No leads. Create one in the Leads page first.</SelectItem>}
                {leadOptions.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.client?.company_name || l.company_name || "Unnamed"} · {l.stage} {l.machine_catalog?.model_name ? `· ${l.machine_catalog.model_name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.lead && <div className="text-xs text-red-600 mt-1" data-testid="error-lead">{errors.lead}</div>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact person</Label>
              <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} placeholder={selectedLead?.contact_person || (!leadTouched.current && booking?.contact?.person) || "From the lead"} className="mt-1" data-testid="booking-contact-person-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact phone</Label>
              <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder={selectedLead?.phone || (!leadTouched.current && booking?.lead?.phone) || "From the lead"} className="mt-1" data-testid="booking-contact-phone-input" />
            </div>
          </div>

          <div>
            <RegionSelect value={regionId} onChange={setRegionId} testId="booking-region-select" />
            {selectedLead?.region_name && !regionId && (
              <div className="text-[11px] text-slate-500 mt-1">
                Will inherit <b>{selectedLead.region_name}</b> from lead
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine model *</Label>
            <Select value={catalogId} onValueChange={(v) => { onCatalogChange(v); setErrors((x) => ({ ...x, machine: "" })); }}>
              <SelectTrigger className={`mt-1 ${errors.machine ? "border-red-500" : ""}`} data-testid="booking-model-select"><SelectValue placeholder="Select a machine model" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {modelOptions.length === 0 && <SelectItem value="none" disabled>No demo-pool models available. Add one in Inventory first.</SelectItem>}
                {modelOptions.map((g) => (
                  <SelectItem key={g.catalog_id} value={g.catalog_id} data-testid={`booking-model-${g.catalog_id}`}>
                    {g.model_name}
                    <span className="ml-2 text-xs text-slate-400">{g.manufacturer}{g.unit_count > 1 ? ` · ${g.unit_count} units` : ""}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.machine && <div className="text-xs text-red-600 mt-1" data-testid="error-machine">{errors.machine}</div>}
            <div className="mt-1 text-[11px] text-slate-500">The system reserves a free unit of this model for your selected dates. If none are free, you'll be asked to pick a different window.</div>
          </div>

          {catalogId && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Serial number</Label>
                <label className="flex flex-wrap items-center justify-end gap-2 text-[11px] text-slate-500 cursor-pointer">
                  <span>Only free serials for this window</span>
                  <Switch className="shrink-0" checked={onlyFree} onCheckedChange={setOnlyFree} disabled={!windowComplete} data-testid="booking-only-free-toggle" />
                </label>
              </div>
              <Select value={inventoryId || "__any__"} onValueChange={(v) => setInventoryId(v === "__any__" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="booking-serial-select"><SelectValue placeholder="Any available serial" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__any__" data-testid="booking-serial-any">Any available serial (auto-pick)</SelectItem>
                  {visibleUnits.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`booking-serial-${u.id}`}>
                      {u.serial_number || u.id.slice(0, 8)}
                      <span className="ml-2 text-xs text-slate-400">
                        {u.current ? "current unit · " : ""}{u.location}
                        {windowComplete && u.busy_in_window
                          ? ` · busy ${formatDMY(u.busy_window?.scheduled_at)}→${formatDMY(u.busy_window?.expected_return_date)}`
                          : ""}
                      </span>
                    </SelectItem>
                  ))}
                  {visibleUnits.length === 0 && (
                    <SelectItem value="__none__" disabled data-testid="booking-serial-empty">
                      No serials free for this window — leave on Any or widen the window
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <div className="mt-1 text-[11px] text-slate-500">
                {windowComplete
                  ? (onlyFree
                      ? (hiddenCount > 0 ? `${hiddenCount} serial${hiddenCount === 1 ? "" : "s"} hidden — already booked in your window.` : "All listed serials are free for your window.")
                      : "Toggle on to hide serials already booked in your window.")
                  : "Pick the demo window above to enable the free-only filter."}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Demo date *</Label>
              <Input type="date" required value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className={`mt-1 ${scheduleError ? "border-red-500 focus-visible:ring-red-500" : ""}`} data-testid="booking-date-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Demo time *</Label>
              <Input type="time" required value={scheduledTime} onChange={(e) => onTimeChange(e.target.value)} className={`mt-1 ${scheduleError ? "border-red-500 focus-visible:ring-red-500" : ""}`} data-testid="booking-time-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Return date *</Label>
              <Input type="date" required value={expectedReturn} onChange={(e) => { setExpectedReturn(e.target.value); setErrors((x) => ({ ...x, ret: "" })); }} className={`mt-1 ${errors.ret ? "border-red-500" : ""}`} data-testid="booking-return-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Return time *</Label>
              <Input type="time" required value={expectedReturnTime} onChange={(e) => { setExpectedReturnTime(e.target.value); setErrors((x) => ({ ...x, ret: "" })); }} className={`mt-1 ${errors.ret ? "border-red-500" : ""}`} data-testid="booking-return-time-input" />
            </div>
          </div>
          {errors.scheduled && <div className="text-xs text-red-600 -mt-2" data-testid="error-scheduled">{errors.scheduled}</div>}
          {errors.ret && <div className="text-xs text-red-600 -mt-2" data-testid="error-return">{errors.ret}</div>}
          {scheduleError && (
            <div className="text-xs text-red-600 -mt-2" data-testid="schedule-error">{scheduleError}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Engineer visit from *</Label>
              <Input type="time" required value={visitFrom} onChange={(e) => setVisitFrom(e.target.value)} className="mt-1" data-testid="booking-visit-from-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Engineer visit to *</Label>
              <Input type="time" required value={visitTo} onChange={(e) => setVisitTo(e.target.value)} className="mt-1" data-testid="booking-visit-to-input" />
            </div>
            <div className="col-span-2 -mt-1 text-[11px] text-slate-500">The engineer's on-site slot on the demo date. Used for the schedule and clash checks.</div>
            {errors.visit && <div className="col-span-2 text-xs text-red-600" data-testid="error-visit">{errors.visit}</div>}
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned engineer *</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo} disabled={!canAssignOthers}>
              <SelectTrigger className="mt-1" data-testid="booking-assignee-select"><SelectValue placeholder="Choose an engineer" /></SelectTrigger>
              <SelectContent>
                {engineerOptions.length === 0 && <SelectItem value="none" disabled>No active employees.</SelectItem>}
                {engineerOptions.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}{u.job_title ? ` · ${u.job_title}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!canAssignOthers && <div className="mt-1 text-[11px] text-slate-500">Only managers and admins can assign a visit to someone else.</div>}
            {errors.engineer && <div className="text-xs text-red-600 mt-1" data-testid="error-engineer">{errors.engineer}</div>}
          </div>

          {isEdit && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="booking-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...new Set([...EDIT_STATUSES, booking.status])].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site address *</Label>
            <Select value={addrChoice} onValueChange={(v) => { setAddrChoice(v); setErrors((x) => ({ ...x, address: "" })); }} disabled={!leadId}>
              <SelectTrigger className={`mt-1 ${errors.address ? "border-red-500" : ""}`} data-testid="booking-address-select"><SelectValue placeholder={leadId ? "Choose address" : "Select a lead first"} /></SelectTrigger>
              <SelectContent>
                {client?.billing_address && <SelectItem value="__billing__">Billing address — {client.billing_address.slice(0, 40)}</SelectItem>}
                {(client?.shipping_addresses || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.label} — {a.line.slice(0, 40)}</SelectItem>
                ))}
                <SelectItem value="__override__">{isEdit ? "— Custom address (edit below) —" : "— Enter a different address for this demo —"}</SelectItem>
              </SelectContent>
            </Select>
            {errors.address && <div className="text-xs text-red-600 mt-1" data-testid="error-address">{errors.address}</div>}
            {addrChoice === "__override__" && (
              <div className="mt-3 grid grid-cols-1 gap-2 border border-slate-200 rounded-md p-3 bg-slate-50">
                <Input placeholder="Label (e.g. Trade Show Hall B)" value={override.label} onChange={(e) => setOverride({ ...override, label: e.target.value })} data-testid="override-label-input" />
                <Textarea placeholder="Address line *" rows={2} value={override.line} onChange={(e) => setOverride({ ...override, line: e.target.value })} data-testid="override-line-input" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input placeholder="City" value={override.city} onChange={(e) => setOverride({ ...override, city: e.target.value })} data-testid="override-city-input" />
                  <Input placeholder="State" value={override.state} onChange={(e) => setOverride({ ...override, state: e.target.value })} />
                  <Input placeholder="Pincode" value={override.pincode} onChange={(e) => setOverride({ ...override, pincode: e.target.value })} />
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{isEdit ? "Outcome notes" : "Notes (optional)"}</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="booking-notes-input" />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !!scheduleError} className="bg-indigo-600 hover:bg-indigo-700" data-testid="booking-save-btn">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Create booking"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Detail dialog ----------
function BookingDetailDialog({ booking, users, guard, onEdit, onClose, onChanged }) {
  const [b, setB] = useState(booking);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    setB(booking);
    if (!booking?.id) { setHistory([]); return; }
    api.get(`/bookings/${booking.id}/history`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, [booking]);

  if (!b) return null;
  const canEdit = !!b.can_edit;

  const changeStatus = async (status) => {
    const res = await guard.run((allow) => api.patch(`/bookings/${b.id}/status${allow ? "?allow_clash=true" : ""}`, { status }));
    if (!res) return;
    setB(res.data);
    toast.success(`Status: ${status}`);
    onChanged?.(res.data);
  };

  const cancelBooking = async () => {
    if (!window.confirm("Cancel this booking? The unit is freed and the engineer is notified.")) return;
    try {
      await api.delete(`/bookings/${b.id}`);
      toast.success("Booking cancelled");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={!!b} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Demo booking</span>
            <Badge className={`border ${STATUS_STYLES[b.status] || ""}`} variant="outline" data-testid="detail-status">{b.status}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Client</div>
              <div className="font-semibold text-slate-900" data-testid="detail-client">{b.client?.company_name || "—"}</div>
              <div className="text-slate-500" data-testid="detail-contact">{[b.contact?.person, b.contact?.phone].filter(Boolean).join(" · ")}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine (serial)</div>
              <div className="font-semibold text-slate-900">{b.machine_catalog?.model_name || "—"}</div>
              <div className="font-mono text-slate-500 text-xs" data-testid="detail-serial">{b.inventory?.serial_number}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Scheduled</div>
              <div className="text-slate-900">{fmtDate(b.scheduled_at)} · {fmtTime(b.scheduled_at)}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Expected return</div>
              <div className="text-slate-900" data-testid="detail-return">{fmtDate(b.expected_return_date)}{b.expected_return_date && b.expected_return_date.includes("T") ? ` · ${fmtTime(b.expected_return_date)}` : ""}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned engineer</div>
              <div className="text-slate-900" data-testid="detail-engineer">{b.assigned_user?.name || (users || []).find((u) => u.id === b.assigned_to)?.name || b.assigned_to || "—"}</div>
              {b.visit_start && <div className="text-xs text-slate-500" data-testid="detail-visit-slot">Visit {fmtTime(b.visit_start)} – {fmtTime(b.visit_end)}</div>}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Unit status</div>
              <div className="text-slate-900">{b.inventory?.status}</div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Site address</div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
              <div className="font-medium text-slate-900" data-testid="detail-address-label">{b.site_address?.label || "—"}</div>
              <div className="text-slate-700" data-testid="detail-address-line">{b.site_address?.line}</div>
              <div className="text-slate-500 text-xs">{[b.site_address?.city, b.site_address?.state, b.site_address?.pincode].filter(Boolean).join(", ")}</div>
            </div>
          </div>

          {b.outcome_notes && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Outcome notes</div>
              <div className="text-sm text-slate-700 whitespace-pre-line" data-testid="detail-notes">{b.outcome_notes}</div>
            </div>
          )}

          {canEdit ? (
            <div className="border-t border-slate-200 pt-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Quick status</div>
              <div className="flex flex-wrap gap-2">
                {EDIT_STATUSES.map((s) => (
                  <Button key={s} variant={b.status === s ? "default" : "outline"} size="sm" onClick={() => changeStatus(s)} disabled={b.status === s}
                    className={b.status === s ? "bg-indigo-600 hover:bg-indigo-700" : ""} data-testid={`status-btn-${s.replace(/\s+/g, "-")}`}>{s}</Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="detail-readonly-note">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {["Completed", "Returned", "Converted to Sale", "Cancelled"].includes(b.status)
                ? `This booking is ${b.status}. Only an Admin can change it.`
                : "Read-only. Only the assigned engineer, the booking creator, their manager or an admin can edit this booking."}
            </div>
          )}

          <BookingHistory history={history} />

          <div className="flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-4">
            {canEdit && b.status !== "Cancelled" ? (
              <Button variant="outline" onClick={cancelBooking} data-testid="cancel-booking-btn" className="text-red-600 hover:text-red-700">
                <X className="w-4 h-4 mr-1" /> Cancel booking
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              {canEdit && <Button onClick={() => onEdit?.(b)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="detail-edit-btn"><Pencil className="w-4 h-4 mr-1" /> Edit booking</Button>}
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return `${fmtDate(v)} ${fmtTime(v)}`;
  return typeof v === "object" ? JSON.stringify(v) : String(v);
};

function BookingHistory({ history }) {
  return (
    <div className="border-t border-slate-200 pt-4" data-testid="booking-history">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><History className="w-3.5 h-3.5" /> History</div>
      {history.length === 0 ? <div className="text-xs text-slate-400" data-testid="booking-history-empty">No edits yet.</div> : (
        <ol className="space-y-3">
          {history.map((h, i) => (
            <li key={i} className="text-xs" data-testid={`booking-history-entry-${i}`}>
              <div className="text-slate-500"><b className="text-slate-800">{h.actor_name}</b> · {fmtVal(h.at)} · {h.change}{h.clash_override ? " · clash overridden" : ""}</div>
              <ul className="mt-1 space-y-0.5">
                {h.changes.map((c, j) => (
                  <li key={j} className="text-slate-700"><span className="capitalize text-slate-500">{c.label}:</span> <span className="line-through text-slate-400">{fmtVal(c.before)}</span> → <span className="font-medium">{fmtVal(c.after)}</span></li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------- By-Region grouped view ----------
const BOOKING_STATUSES = STATUSES;

function BookingsByRegion({ bookings, onOpen, onEdit, STATUS_STYLES, fmtDate, fmtTime }) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));

  const groups = {};
  (bookings || []).forEach((b) => {
    const key = b.region_id || "__unassigned__";
    if (!groups[key]) groups[key] = {
      name: b.region_name || (key === "__unassigned__" ? "Unassigned Region" : "Unknown region"),
      bookings: [], statusCounts: {},
    };
    groups[key].bookings.push(b);
    groups[key].statusCounts[b.status] = (groups[key].statusCounts[b.status] || 0) + 1;
  });

  const list = Object.entries(groups)
    .map(([id, g]) => ({ id, ...g }))
    .sort((a, b) => {
      if (a.id === "__unassigned__") return 1;
      if (b.id === "__unassigned__") return -1;
      return b.bookings.length - a.bookings.length;
    });

  if (list.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg py-14 text-center text-slate-400" data-testid="bookings-byregion-empty">
        <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        No bookings to group yet.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="bookings-byregion-view">
      {list.map((g) => {
        const open = !collapsed[g.id];
        return (
          <section key={g.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`bookings-byregion-group-${g.id}`}>
            <button
              type="button"
              onClick={() => toggle(g.id)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
              data-testid={`bookings-byregion-toggle-${g.id}`}
            >
              {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
              <div className="w-8 h-8 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">
                  {g.name}
                  <span className="text-slate-500 font-normal"> — {g.bookings.length} demo{g.bookings.length === 1 ? "" : "s"}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                  {BOOKING_STATUSES.map((s) => (g.statusCounts[s] || 0) > 0 ? `${g.statusCounts[s]} ${s}` : null)
                    .filter(Boolean).join("  ·  ") || "No demos"}
                </div>
              </div>
            </button>
            {open && (
              <>
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/60">
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">When</TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Client</TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Machine</TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Assigned</TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {g.bookings.map((b) => (
                        <TableRow key={b.id} onClick={() => onOpen(b)} className="hover:bg-slate-50 cursor-pointer" data-testid={`bookings-byregion-row-${b.id}`}>
                          <TableCell className="text-sm text-slate-900">{fmtDate(b.scheduled_at)} <span className="text-slate-500">{fmtTime(b.scheduled_at)}</span></TableCell>
                          <TableCell className="text-sm">{b.client?.company_name || "—"}</TableCell>
                          <TableCell className="text-sm text-slate-600">{b.machine_catalog?.model_name || "—"}</TableCell>
                          <TableCell className="text-sm text-slate-600">{b.assigned_user?.name || "—"}</TableCell>
                          <TableCell><div className="flex items-center gap-1.5"><Badge className={`border ${STATUS_STYLES[b.status] || ""}`} variant="outline">{b.status}</Badge><EditBtn b={b} onEdit={onEdit} testId={`bookings-byregion-edit-${b.id}`} /></div></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <ul className="md:hidden divide-y divide-slate-100 border-t border-slate-100">
                  {g.bookings.map((b) => (
                    <li key={b.id} onClick={() => onOpen(b)} className="p-4 active:bg-slate-50" data-testid={`bookings-byregion-card-m-${b.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{b.client?.company_name || "—"}</div>
                          <div className="text-xs text-slate-500 truncate">{b.machine_catalog?.model_name}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0"><EditBtn b={b} onEdit={onEdit} testId={`bookings-byregion-card-edit-${b.id}`} /><Badge className={`border ${STATUS_STYLES[b.status] || ""} shrink-0 text-[10px]`} variant="outline">{b.status}</Badge></div>
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">{fmtDate(b.scheduled_at)} · {fmtTime(b.scheduled_at)}</div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}

